import React, { useState, useEffect, useRef } from 'react';
import { Avatar, StatusBadge } from '../components/UI';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';

function ConvoList({ items, title, activeLeadId, onSelect }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", overflow: "hidden", background: "var(--bg2)", border: "0.5px solid rgba(83,74,183,0.3)", borderRadius: 16, boxShadow: "0 0 20px rgba(83,74,183,0.06)", flex: 1 }}>
      <div style={{ padding: "12px 16px", borderBottom: "0.5px solid var(--border)", background: "linear-gradient(135deg,rgba(83,74,183,0.08),rgba(29,158,117,0.06))", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#534AB7", boxShadow: "0 0 6px #534AB7" }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.5px" }}>{title}</span>
        <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, color: "#534AB7", background: "rgba(83,74,183,0.1)", padding: "2px 8px", borderRadius: 20 }}>{items.length}</span>
      </div>
      <div style={{ overflow: "auto", flex: 1 }}>
        {items.length === 0 && (
          <div style={{ padding: "28px 16px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: "rgba(83,74,183,0.06)", border: "0.5px solid rgba(83,74,183,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="ti ti-message-off" style={{ fontSize: 18, color: "var(--text3)" }} aria-hidden="true" />
            </div>
            <span style={{ fontSize: 12, color: "var(--text3)", lineHeight: 1.4 }}>No conversations yet</span>
          </div>
        )}
        {items.map(c => (
          <div key={c.lead_id} onClick={() => onSelect(c)}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderBottom: "0.5px solid var(--border)", cursor: "pointer", background: activeLeadId === c.lead_id ? "rgba(83,74,183,0.06)" : "transparent", transition: "background 0.1s" }}
            onMouseEnter={e => { if (activeLeadId !== c.lead_id) e.currentTarget.style.background = "var(--bg3)"; }}
            onMouseLeave={e => { if (activeLeadId !== c.lead_id) e.currentTarget.style.background = "transparent"; }}>
            <Avatar name={c.lead_name || "?"} size={32} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{c.lead_name}</span>
                {c.ai_enabled && <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: "linear-gradient(135deg,#534AB7,#1D9E75)", color: "#fff", fontWeight: 700 }}>AI</span>}
              </div>
              <div style={{ fontSize: 11, color: "var(--text2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.message}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Inbox() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [convo, setConvo] = useState([]);
  const [msg, setMsg] = useState("");
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    const load = () => api.getInbox().then(setConversations).catch(() => {});
    load();
    api.getWorkflows().then(setWorkflows).catch(() => {});
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (active) api.getConversation(active.lead_id).then(setConvo).catch(() => setConvo([]));
  }, [active]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [convo]);

  const handleSend = async () => {
    if (!msg.trim() || !active) return;
    try {
      await api.sendSMS({ to: active.phone, message: msg, lead_id: active.lead_id });
      const updated = await api.getConversation(active.lead_id);
      setConvo(updated); setMsg(''); setSelectedWorkflow('');
      api.getInbox().then(setConversations).catch(() => {});
    } catch (err) { alert(err.message); }
  };

  const unique = conversations.reduce((acc, c) => { if (!acc.find(x => x.lead_id === c.lead_id)) acc.push(c); return acc; }, []);
  const aiLeads = unique.filter(c => c.ai_enabled === true);
  const manualLeads = unique.filter(c => c.ai_enabled !== true);

  return (
    <div style={{ display: "flex", gap: 14, height: "calc(100vh - 92px)" }}>

      {/* Left sidebar - always split */}
      <div style={{ width: 280, flexShrink: 0, display: "flex", flexDirection: "column", gap: 10 }}>
        <ConvoList items={aiLeads} title="AI" activeLeadId={active && active.lead_id} onSelect={setActive} />
        <ConvoList items={manualLeads} title="Manual" activeLeadId={active && active.lead_id} onSelect={setActive} />
      </div>

      {/* Main chat area */}
      <div style={{ flex: 1, background: "var(--bg2)", border: "0.5px solid rgba(83,74,183,0.3)", borderRadius: 16, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 0 30px rgba(83,74,183,0.08)" }}>
        {active ? (
          <>
            {/* Chat header */}
            <div style={{ padding: "14px 20px", borderBottom: "0.5px solid var(--border)", background: "linear-gradient(135deg,rgba(83,74,183,0.08),rgba(29,158,117,0.06))", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
              <Avatar name={active.lead_name || "?"} size={36} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", letterSpacing: "-0.3px" }}>{active.lead_name}</div>
                <div style={{ fontSize: 11, color: "var(--text2)" }}>{active.phone}</div>
              </div>
              <StatusBadge status={active.status} />
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflow: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
              {convo.length === 0 && <div style={{ textAlign: "center", color: "var(--text3)", fontSize: 12, marginTop: 40 }}>No messages yet</div>}
              {convo.map((m, i) => (
                <div key={i} style={{ alignSelf: m.direction === "out" ? "flex-end" : "flex-start", maxWidth: "65%" }}>
                  <div style={{ background: m.direction === "out" ? "linear-gradient(135deg,#534AB7,#3d35a0)" : "var(--bg3)", borderRadius: m.direction === "out" ? "16px 4px 16px 16px" : "4px 16px 16px 16px", padding: "10px 14px", fontSize: 13, lineHeight: 1.5, color: m.direction === "out" ? "#fff" : "var(--text)" }}>
                    {m.message}
                  </div>
                  {m.source && m.source.includes('ai') && <div style={{ fontSize: 10, color: "#534AB7", marginTop: 3, fontWeight: 600, textAlign: "right" }}>FLO AI</div>}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div style={{ padding: "14px 20px", borderTop: "0.5px solid var(--border)", flexShrink: 0 }}>
              {active.ai_enabled === true ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderRadius: 10, background: "rgba(83,74,183,0.06)", border: "0.5px solid rgba(83,74,183,0.2)" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#1D9E75", boxShadow: "0 0 6px #1D9E75" }} />
                  <span style={{ fontSize: 12, color: "var(--text2)", fontStyle: "italic" }}>FLO AI is handling this conversation</span>
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", gap: 10 }}>
                    <input value={msg} onChange={e => { setMsg(e.target.value); setSelectedWorkflow(''); }} onKeyDown={e => e.key === 'Enter' && handleSend()} placeholder="Type a message..." style={{ flex: 1, padding: "10px 14px", borderRadius: 10, border: "0.5px solid var(--border2)", background: "var(--bg3)", fontSize: 13, color: "var(--text)" }} />
                    <button onClick={handleSend} style={{ padding: "10px 22px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#534AB7,#1D9E75)", color: "#fff", fontSize: 13, cursor: "pointer", fontWeight: 600, boxShadow: "0 4px 12px rgba(83,74,183,0.3)" }}>Send</button>
                  </div>
                  {workflows.length > 0 && (
                    <select value={selectedWorkflow} onChange={e => { setSelectedWorkflow(e.target.value); if (e.target.value) { const w = workflows.find(x => (x._id || x.id) === e.target.value); if (w && w.steps && w.steps[0]) setMsg(w.steps[0].message); } }} style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "0.5px solid var(--border2)", background: "var(--bg3)", fontSize: 12, color: "var(--text)", cursor: "pointer", marginTop: 8 }}>
                      <option value="">Select a workflow...</option>
                      {workflows.map(w => <option key={w._id || w.id} value={w._id || w.id}>{w.name}</option>)}
                    </select>
                  )}
                </>
              )}
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 14 }}>
            <div style={{ width: 64, height: 64, borderRadius: 20, background: "linear-gradient(135deg,rgba(83,74,183,0.08),rgba(29,158,117,0.08))", border: "0.5px solid rgba(83,74,183,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="ti ti-messages" style={{ fontSize: 28, color: "var(--text3)" }} aria-hidden="true" />
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 5 }}>No conversation open</div>
              <div style={{ fontSize: 13, color: "var(--text3)", maxWidth: 220, lineHeight: 1.5 }}>Select a lead from the left to view messages.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}